import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, vModelDynamic as _vModelDynamic, withModifiers as _withModifiers, Fragment as _Fragment, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "portal-page__inner auth-page__shell" }
const _hoisted_2 = {
  key: 0,
  class: "signup-done",
  "aria-live": "polite"
}
const _hoisted_3 = { class: "signup-done__desc" }
const _hoisted_4 = { class: "signup-done__meta" }
const _hoisted_5 = { class: "signup-done__row" }
const _hoisted_6 = { class: "signup-done__actions" }
const _hoisted_7 = { class: "auth-form__body" }
const _hoisted_8 = { class: "auth-form__section" }
const _hoisted_9 = { class: "auth-form__field" }
const _hoisted_10 = { class: "auth-form__field-row" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = {
  key: 1,
  class: "auth-form__hint"
}
const _hoisted_13 = { class: "auth-form__field" }
const _hoisted_14 = { class: "signup-secret" }
const _hoisted_15 = ["type"]
const _hoisted_16 = {
  class: "signup-pw-check",
  "aria-label": "비밀번호 조건"
}
const _hoisted_17 = { class: "auth-form__field" }
const _hoisted_18 = { class: "signup-secret" }
const _hoisted_19 = ["type", "placeholder", "disabled"]
const _hoisted_20 = {
  key: 0,
  class: "signup-step-error",
  role: "alert"
}
const _hoisted_21 = { class: "signup-nav" }
const _hoisted_22 = ["disabled"]

import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import TermsAgreementSection, { type AgreementState } from '@/components/auth/TermsAgreementSection.vue'
import { readSignupDone, saveSignupDone } from '@/constants/authPrefill'
import { clearOAuthSignup } from '@/constants/oauthSignup'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { checkMemberUserId, signUpMember } from '@/api/member'
import { saveAutoLogin } from '@/constants/autoLogin'
import {
  collectSignUpValidationErrors,
  inputValidationState,
  isPasswordConfirmValid,
  isPasswordValid,
  isUserIdFormatValid,
  sanitizeUserIdInput,
  USER_ID_PATTERN,
} from '@/utils/formValidation'


export default /*@__PURE__*/_defineComponent({
  __name: 'SignUpPage',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const { isLoggedIn, setMember } = useMemberAuth()

const signupDone = ref(false)
const doneUserId = ref('')
const showPassword = ref(false)
const skipLeaveGuard = ref(false)
const isSubmitting = ref(false)
const errorMessage = ref('')
const confirmedUserId = ref('')
const userIdMessage = ref('')

const form = reactive({
  userId: '',
  password: '',
  passwordConfirm: '',
})

const agreements = reactive<AgreementState>({
  age: false,
  terms: false,
  privacy: false,
  marketing: false,
})

function onAgreementsUpdate(next: AgreementState) {
  agreements.age = next.age
  agreements.terms = next.terms
  agreements.privacy = next.privacy
  agreements.marketing = next.marketing
}

const isMallSignup = computed(() => route.meta.layout === 'mall')

const userIdChecked = computed(
  () => confirmedUserId.value !== '' && form.userId === confirmedUserId.value,
)
const passwordLongEnough = computed(() => form.password.length >= 8)
const passwordHasLetter = computed(() => /[A-Za-z]/.test(form.password))
const passwordHasDigit = computed(() => /\d/.test(form.password))
const passwordHasSpecial = computed(() => /[^A-Za-z0-9]/.test(form.password))
const passwordReady = computed(() => isPasswordValid(form.password))
const passwordMatched = computed(() => isPasswordConfirmValid(form.passwordConfirm, form.password))
const userIdInputState = computed(() =>
  inputValidationState(form.userId, isUserIdFormatValid(form.userId) && userIdChecked.value),
)
const passwordInputState = computed(() => inputValidationState(form.password, isPasswordValid(form.password)))
const passwordConfirmInputState = computed(() =>
  inputValidationState(form.passwordConfirm, passwordMatched.value),
)
const hasDraft = computed(
  () => Boolean(form.userId || form.password || form.passwordConfirm || agreements.terms),
)

watch(passwordReady, (ready) => {
  if (!ready) form.passwordConfirm = ''
})

const showSignupDone = (userId: string) => {
  skipLeaveGuard.value = true
  doneUserId.value = userId
  signupDone.value = true
}

function onUserIdInput() {
  form.userId = sanitizeUserIdInput(form.userId)
  if (form.userId !== confirmedUserId.value) {
    confirmedUserId.value = ''
  }
}

function onUserIdFocus() {
  if (form.userId !== confirmedUserId.value) {
    userIdMessage.value = ''
  }
}

function onUserIdBlur() {
  if (isUserIdFormatValid(form.userId) && !userIdChecked.value) {
    void checkUserId()
  }
}

const checkUserId = async () => {
  if (!USER_ID_PATTERN.test(form.userId)) {
    userIdMessage.value = '아이디 형식이 올바르지 않습니다.'
    confirmedUserId.value = ''
    return
  }

  try {
    const result = await checkMemberUserId(form.userId)
    if (result.available) {
      confirmedUserId.value = form.userId
      userIdMessage.value = '사용할 수 있는 아이디입니다.'
      return
    }
    confirmedUserId.value = ''
    userIdMessage.value = '이미 쓰고 있는 아이디입니다. 다른 아이디를 적어 주세요.'
  } catch (error) {
    confirmedUserId.value = ''
    userIdMessage.value = error instanceof Error ? error.message : '아이디 확인에 실패했습니다.'
  }
}

onBeforeRouteLeave(() => {
  if (skipLeaveGuard.value || !hasDraft.value) {
    return true
  }
  return window.confirm('지금까지 적은 내용이 사라집니다.\n다른 화면으로 나가시겠습니까?')
})

const onBeforeUnload = (event: BeforeUnloadEvent) => {
  if (skipLeaveGuard.value || !hasDraft.value) {
    return
  }
  event.preventDefault()
  event.returnValue = ''
}

onMounted(() => {
  window.addEventListener('beforeunload', onBeforeUnload)
  clearOAuthSignup()

  if (route.query.done === '1') {
    const snapshot = readSignupDone()
    if (snapshot) {
      showSignupDone(snapshot.userId)
    }
  }
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', onBeforeUnload)
})

const onSubmit = async () => {
  errorMessage.value = ''
  const errors = collectSignUpValidationErrors({
    form: {
      ...form,
      name: '',
      email: '',
      phone: '',
      orgName: '',
      orgCode: '',
      bizGubun: '',
      bizNum: '',
      bizCert: '',
      zipcode: '',
      address: '',
      addressDetail: '',
      refundBank: '',
      refundAccount: '',
      refundHolder: '',
    },
    userIdChecked: userIdChecked.value,
    orgCodeChecked: true,
    emailVerified: true,
    phoneVerified: true,
    agreements,
  })
  if (errors.length) {
    errorMessage.value = errors[0]
    return
  }

  if (isSubmitting.value) {
    return
  }
  isSubmitting.value = true

  try {
    const created = await signUpMember({
      userId: form.userId,
      password: form.password,
      passwordConfirm: form.passwordConfirm,
      agreements: { ...agreements },
    })

    if (created.member) {
      saveAutoLogin('member', true)
      setMember(created.member)
    }
    saveSignupDone({
      name: form.userId,
      userId: form.userId,
    })
    showSignupDone(created.userId || form.userId)
    await router.replace({ query: { ...route.query, done: '1' } })
    await nextTick()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '회원가입에 실패했습니다.'
  } finally {
    isSubmitting.value = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["portal-page auth-page signup-page", { 'signup-page--mall': isMallSignup.value }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (signupDone.value)
        ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
            _cache[10] || (_cache[10] = _createStaticVNode("<span class=\"signup-done__mark\" aria-hidden=\"true\"><svg viewBox=\"0 0 24 24\" width=\"36\" height=\"36\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\"><path d=\"M4 12.5l5.5 5.5L20 7\" stroke-linecap=\"round\" stroke-linejoin=\"round\"></path></svg></span><p class=\"signup-done__eyebrow\">회원가입 완료</p><h1 class=\"signup-done__title\">가입을 축하드립니다</h1>", 3)),
            _createElementVNode("p", _hoisted_3, [
              _createElementVNode("strong", null, _toDisplayString(doneUserId.value), 1),
              _cache[5] || (_cache[5] = _createTextVNode(" 님, 함우리몰을 바로 이용할 수 있습니다. 배송지와 연락처는 처음 주문할 때 받습니다. ", -1))
            ]),
            _createElementVNode("dl", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _cache[6] || (_cache[6] = _createElementVNode("dt", null, "로그인 아이디", -1)),
                _createElementVNode("dd", null, _toDisplayString(doneUserId.value), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_6, [
              (_unref(isLoggedIn))
                ? (_openBlock(), _createBlock(_component_router_link, {
                    key: 0,
                    to: "/mall",
                    class: "btn-primary"
                  }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode("함우리몰 둘러보기", -1)
                    ]))]),
                    _: 1
                  }))
                : (_openBlock(), _createBlock(_component_router_link, {
                    key: 1,
                    to: { name: 'login', query: { joined: '1' } },
                    class: "btn-primary"
                  }, {
                    default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                      _createTextVNode(" 로그인하기 ", -1)
                    ]))]),
                    _: 1
                  })),
              _createVNode(_component_router_link, {
                to: "/",
                class: "btn-secondary"
              }, {
                default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                  _createTextVNode("홈으로", -1)
                ]))]),
                _: 1
              })
            ])
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _cache[14] || (_cache[14] = _createElementVNode("header", { class: "auth-page__head" }, [
              _createElementVNode("h1", { class: "auth-page__title" }, "회원가입")
            ], -1)),
            _createElementVNode("form", {
              class: "auth-page__panel auth-form signup-form",
              onSubmit: _withModifiers(onSubmit, ["prevent"])
            }, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("section", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _cache[11] || (_cache[11] = _createElementVNode("span", null, "아이디", -1)),
                    _createElementVNode("div", _hoisted_10, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.userId) = $event)),
                        type: "text",
                        autocomplete: "username",
                        placeholder: "영문, 숫자 (4~20자)",
                        maxlength: "20",
                        autocapitalize: "none",
                        autocorrect: "off",
                        spellcheck: "false",
                        class: _normalizeClass(userIdInputState.value),
                        onInput: onUserIdInput,
                        onFocus: onUserIdFocus,
                        onBlur: onUserIdBlur
                      }, null, 34), [
                        [_vModelText, form.userId]
                      ]),
                      _createElementVNode("button", {
                        type: "button",
                        class: _normalizeClass(["auth-form__inline-btn", { 'auth-form__inline-btn--done': userIdChecked.value }]),
                        disabled: !_unref(isUserIdFormatValid)(form.userId) || userIdChecked.value,
                        onClick: checkUserId
                      }, _toDisplayString(userIdChecked.value ? '확인됨' : '확인'), 11, _hoisted_11)
                    ]),
                    (userIdMessage.value)
                      ? (_openBlock(), _createElementBlock("p", {
                          key: 0,
                          class: _normalizeClass(["auth-form__msg", userIdChecked.value ? 'auth-form__msg--ok' : 'auth-form__msg--warn'])
                        }, _toDisplayString(userIdMessage.value), 3))
                      : (_openBlock(), _createElementBlock("p", _hoisted_12, "로그인할 때 쓸 아이디입니다. 한글은 안 됩니다."))
                  ]),
                  _createElementVNode("label", _hoisted_13, [
                    _cache[12] || (_cache[12] = _createElementVNode("span", null, "비밀번호", -1)),
                    _createElementVNode("div", _hoisted_14, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.password) = $event)),
                        type: showPassword.value ? 'text' : 'password',
                        autocomplete: "new-password",
                        placeholder: "8자 이상",
                        class: _normalizeClass(passwordInputState.value)
                      }, null, 10, _hoisted_15), [
                        [_vModelDynamic, form.password]
                      ]),
                      _createElementVNode("button", {
                        type: "button",
                        class: "signup-secret__toggle",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (showPassword.value = !showPassword.value))
                      }, _toDisplayString(showPassword.value ? '숨기기' : '보기'), 1)
                    ]),
                    _createElementVNode("ul", _hoisted_16, [
                      _createElementVNode("li", {
                        class: _normalizeClass({ 'signup-pw-check__ok': passwordLongEnough.value })
                      }, "8자 이상", 2),
                      _createElementVNode("li", {
                        class: _normalizeClass({ 'signup-pw-check__ok': passwordHasLetter.value })
                      }, "영문", 2),
                      _createElementVNode("li", {
                        class: _normalizeClass({ 'signup-pw-check__ok': passwordHasDigit.value })
                      }, "숫자", 2),
                      _createElementVNode("li", {
                        class: _normalizeClass({ 'signup-pw-check__ok': passwordHasSpecial.value })
                      }, "특수문자 !@#", 2)
                    ])
                  ]),
                  _createElementVNode("label", _hoisted_17, [
                    _cache[13] || (_cache[13] = _createElementVNode("span", null, "비밀번호 다시 입력", -1)),
                    _createElementVNode("div", _hoisted_18, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.passwordConfirm) = $event)),
                        type: showPassword.value ? 'text' : 'password',
                        autocomplete: "new-password",
                        placeholder: passwordReady.value ? '방금 만든 비밀번호를 다시 적어 주세요' : '비밀번호 기준을 먼저 충족해 주세요',
                        disabled: !passwordReady.value,
                        class: _normalizeClass(passwordConfirmInputState.value)
                      }, null, 10, _hoisted_19), [
                        [_vModelDynamic, form.passwordConfirm]
                      ]),
                      _createElementVNode("button", {
                        type: "button",
                        class: "signup-secret__toggle",
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (showPassword.value = !showPassword.value))
                      }, _toDisplayString(showPassword.value ? '숨기기' : '보기'), 1)
                    ]),
                    (form.passwordConfirm)
                      ? (_openBlock(), _createElementBlock("p", {
                          key: 0,
                          class: _normalizeClass(["auth-form__msg", passwordMatched.value ? 'auth-form__msg--ok' : 'auth-form__msg--warn'])
                        }, _toDisplayString(passwordMatched.value ? '비밀번호가 같습니다.' : '비밀번호가 다릅니다. 다시 봐 주세요.'), 3))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createVNode(TermsAgreementSection, {
                  "model-value": agreements,
                  "onUpdate:modelValue": onAgreementsUpdate
                }, null, 8, ["model-value"])
              ]),
              (errorMessage.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_20, _toDisplayString(errorMessage.value), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_21, [
                _createElementVNode("button", {
                  type: "submit",
                  class: "btn-primary signup-nav__next",
                  disabled: isSubmitting.value
                }, _toDisplayString(isSubmitting.value ? '가입하는 중...' : '가입하기'), 9, _hoisted_22)
              ])
            ], 32)
          ], 64))
    ])
  ], 2))
}
}

})